import { DeleteOutlined, PlusOutlined } from '@ant-design/icons';
import { SupplierAdditionalContact } from '@gooduncles/gu-app-schema';
import { ICellEditorParams } from 'ag-grid-community';
import { Button, Input, Space, message } from 'antd';
import { forwardRef, useImperativeHandle, useState } from 'react';
import { normalizeTel } from 'src/utils/common';

/**
 * additionalContacts 필드를 위한 셀 에디터 컴포넌트
 * ProductSupplierDoc의 additionalContacts 필드를 수정하기 위한 컴포넌트입니다.
 */
const AgCellAdditionalContacts = forwardRef<any, ICellEditorParams>((props, ref) => {
  // 초기 연락처 목록 설정
  const [contacts, setContacts] = useState<SupplierAdditionalContact[]>(props.value ? [...props.value] : []);
  // 새 연락처 입력을 위한 상태
  const [newLabel, setNewLabel] = useState<string>('');
  const [newNumber, setNewNumber] = useState<string>('');

  // 에디터 인터페이스 설정
  useImperativeHandle(ref, () => {
    return {
      // 값을 가져오는 메서드 (ag-grid가 호출)
      getValue() {
        return contacts;
      },

      // 에디터가 포커스될 때 호출
      afterGuiAttached() {
        // 필요한 경우 초기화 로직
      },
    };
  });

  // 유효성 검사 함수
  const validateContact = (label: string, number: string): boolean => {
    if (!label.trim()) {
      return false;
    }
    if (!number || number.length < 10) {
      return false;
    }
    return true;
  };

  // 새 연락처 항목 추가
  const addContact = () => {
    if (!newLabel.trim()) {
      message.error('연락처 구분을 입력해주세요.');
      return;
    }

    if (!newNumber) {
      message.error('연락처 번호를 입력해주세요.');
      return;
    }

    if (newNumber.length < 10) {
      message.error('연락처 번호는 최소 10자 이상이어야 합니다.');
      return;
    }

    setContacts([...contacts, { label: newLabel, number: newNumber }]);
    // 입력 필드 초기화
    setNewLabel('');
    setNewNumber('');
  };

  // 연락처 항목 삭제
  const removeContact = (index: number) => {
    setContacts(contacts.filter((_, i) => i !== index));
  };

  // 라벨 변경 핸들러
  const handleLabelChange = (index: number, value: string) => {
    const newContacts = [...contacts];
    newContacts[index].label = value;
    setContacts(newContacts);
  };

  // 번호 변경 핸들러
  const handleNumberChange = (index: number, value: string) => {
    const newContacts = [...contacts];
    // normalizeTel 함수를 사용하여 전화번호 형식 정규화
    newContacts[index].number = normalizeTel(value);
    setContacts(newContacts);
  };

  return (
    <div style={{ padding: '10px', minWidth: '300px', maxHeight: '200px', overflowY: 'auto' }}>
      {contacts.map((contact, index) => (
        <Space key={index} style={{ display: 'flex', marginBottom: '8px' }}>
          <Input
            placeholder='구분'
            value={contact.label}
            onChange={(e) => handleLabelChange(index, e.target.value)}
            style={{ width: '100px' }}
            status={!contact.label.trim() ? 'error' : ''}
          />
          <Input
            placeholder='연락처'
            value={contact.number}
            onChange={(e) => handleNumberChange(index, e.target.value)}
            style={{ width: '150px' }}
            status={contact.number.length < 10 ? 'error' : ''}
          />
          <Button icon={<DeleteOutlined />} onClick={() => removeContact(index)} danger size='small' />
        </Space>
      ))}

      {/* 새 연락처 추가 폼 */}
      <Space style={{ display: 'flex', marginBottom: '8px' }}>
        <Input
          placeholder='구분'
          value={newLabel}
          onChange={(e) => setNewLabel(e.target.value)}
          style={{ width: '100px' }}
          status={newLabel && !newLabel.trim() ? 'error' : ''}
        />
        <Input
          placeholder='연락처'
          value={newNumber}
          onChange={(e) => setNewNumber(normalizeTel(e.target.value))}
          style={{ width: '150px' }}
          status={newNumber && newNumber.length < 10 ? 'error' : ''}
        />
        <Button
          type='primary'
          size='small'
          icon={<PlusOutlined />}
          onClick={addContact}
          disabled={!validateContact(newLabel, newNumber)}
        />
      </Space>

      {/* 안내 메시지 */}
      <div style={{ fontSize: '12px', color: '#888', marginTop: '8px' }}>
        연락처 번호는 최소 10자 이상이어야 합니다.
      </div>
    </div>
  );
});

// display name 설정
AgCellAdditionalContacts.displayName = 'AgCellAdditionalContacts';

export default AgCellAdditionalContacts;
